// ============================================================================
// CI/CD 워크플로우 섹션 (CHG-048: REQ-V48-011)
// 이슈 상세 개발 현황 패널 내 CI/CD 표시
// ============================================================================

import { useState } from 'react';
import { Play, ChevronDown, ChevronUp, ExternalLink, Loader2 } from 'lucide-react';
import { useIssueWorkflows } from '../../hooks/useReleases';

interface Props {
  projectId: string;
  issueId: string;
}

export default function WorkflowSection({ projectId, issueId }: Props) {
  const { data: workflows } = useIssueWorkflows(projectId, issueId);
  const [showAll, setShowAll] = useState(false);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);

  if (!workflows || workflows.length === 0) return null;

  const displayed = showAll ? workflows : workflows.slice(0, 5);

  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-500 mb-1">
        <Play className="size-3.5" /> CI/CD <span className="text-gray-400">({workflows.length})</span>
      </div>
      <div className="ml-1 border-l-2 border-gray-100 pl-3 space-y-1">
        {displayed.map((run) => {
          const isExpanded = expandedRun === run.runId;
          return (
            <div key={run.runId}>
              <div
                className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                onClick={() => setExpandedRun(isExpanded ? null : run.runId)}
              >
                <ConclusionIcon conclusion={run.conclusion} status={run.status} />
                <span className="text-sm text-gray-700 truncate flex-1">{run.workflowName}</span>
                {run.durationSeconds && (
                  <span className="text-[11px] text-gray-400">{formatDuration(run.durationSeconds)}</span>
                )}
                <a
                  href={run.htmlUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="size-3" />
                </a>
                {run.jobs.length > 0 && (
                  isExpanded ? <ChevronUp className="size-3 text-gray-400" /> : <ChevronDown className="size-3 text-gray-400" />
                )}
              </div>
              {isExpanded && run.jobs.length > 0 && (
                <div className="ml-4 mt-1 space-y-0.5 mb-1">
                  {run.jobs.map((job) => (
                    <div
                      key={job.jobId}
                      className={`flex items-center gap-2 px-2 py-0.5 rounded text-[11px] ${job.conclusion === 'failure' ? 'bg-red-50' : 'bg-gray-50'}`}
                    >
                      <ConclusionIcon conclusion={job.conclusion} status={job.status} small />
                      <span className="text-gray-700 flex-1">{job.name}</span>
                      {job.durationSeconds && <span className="text-gray-400">{formatDuration(job.durationSeconds)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {workflows.length > 5 && !showAll && (
          <button onClick={() => setShowAll(true)} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <ChevronDown className="size-3" /> {workflows.length - 5}개 더보기
          </button>
        )}
      </div>
    </div>
  );
}

function ConclusionIcon({ conclusion, status, small }: { conclusion: string | null; status: string; small?: boolean }) {
  const size = small ? 'text-[11px]' : 'text-sm';
  if (status === 'in_progress' || status === 'IN_PROGRESS') return <Loader2 className={`${small ? 'size-3' : 'size-3.5'} animate-spin text-blue-500`} />;
  switch (conclusion) {
    case 'success': case 'SUCCESS': return <span className={size}>✅</span>;
    case 'failure': case 'FAILURE': return <span className={size}>❌</span>;
    case 'cancelled': case 'CANCELLED': return <span className={size}>⏸️</span>;
    default: return <span className={size}>⏳</span>;
  }
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
